import { BasePagination, Box, IconButton, Text, Tooltip, useCustomToast, useDisclosure } from '@elkaso-app/web-design';
import {
  ICrmSupplierContactQuotaData,
  useDeleteCrmSupplierContactQuotaApi,
  useGetCrmSupplierContactQuotasApi,
} from 'apis/crm-supplier-contact-quotas';
import { BaseSortButton, BaseTable, BaseTableContainer } from 'components/base-table';
import {
  BaseTableSpinner,
  BaseTableTitle,
  ClearFilterButton,
  CustomTableCell,
  TableHeader,
} from 'components/base-table/components';
import { useSetPaginationParamsV1 } from 'hooks/use-set-pagination-params-v1';
import React, { useEffect, useState } from 'react';
import { FiPlus } from 'react-icons/fi';
import { CellProps } from 'react-table';
import { changeTitle } from 'redux/actions';
import { useAppDispatch } from 'redux/hooks';
import { QuotaRangeFilterButton } from '../components/filters/quota-range-filter';
import { SupplierFilterButton } from '../components/filters/supplier-filter';
import { ContactHistoryModal } from '../components/modals/contact-history-modal';
import { CreateQuotaModal } from '../components/modals/create-quota-modal';
import { EditQuotaModal } from '../components/modals/edit-quota-modal';
import { ViewQuotaModal } from '../components/modals/view-quota-modal';
import { StatsCards } from '../components/stats-cards';
import { ActionsCell, QuotaInfoCell, SupplierCell } from '../components/table-cells';

const CrmSupplierContactQuotasListScreen = () => {
  const setPaginationParams = useSetPaginationParamsV1();
  const dispatch = useAppDispatch();
  const toast = useCustomToast();

  // Modals state
  const { isOpen: isCreateOpen, onOpen: onCreateOpen, onClose: onCreateClose } = useDisclosure();
  const { isOpen: isViewOpen, onOpen: onViewOpen, onClose: onViewClose } = useDisclosure();
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();
  const { isOpen: isHistoryOpen, onOpen: onHistoryOpen, onClose: onHistoryClose } = useDisclosure();
  const [selectedQuotaId, setSelectedQuotaId] = useState<number | null>(null);
  const [selectedSupplier, setSelectedSupplier] = useState<{ id: number; name: string } | null>(null);

  // API hooks
  const { isLoading, isFetching, data } = useGetCrmSupplierContactQuotasApi({
    onError: () => {
      toast({
        title: 'Failed to Load Data',
        description: 'Unable to load supplier contact quotas. Please check your connection and try again.',
        status: 'error',
        duration: 7000,
        isClosable: true,
      });
    },
  });

  const { mutate: deleteQuota, isLoading: isDeleting } = useDeleteCrmSupplierContactQuotaApi({
    onSuccess: () => {
      toast({
        title: 'Quota Deleted Successfully!',
        description: 'The supplier contact quota has been permanently removed.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Delete Failed',
        description: error?.response?.data?.message || 'Failed to delete quota. Please try again.',
        status: 'error',
        duration: 7000,
        isClosable: true,
      });
    },
  });

  const { pageCount, total, data: quotasData } = data ?? {};

  // Handlers
  const handleViewQuota = (quotaId: number) => {
    setSelectedQuotaId(quotaId);
    onViewOpen();
  };

  const handleEditQuota = (quotaId: number) => {
    setSelectedQuotaId(quotaId);
    onEditOpen();
  };

  const handleDeleteQuota = (quotaId: number) => {
    if (window.confirm('Are you sure you want to delete this quota? This action cannot be undone.')) {
      deleteQuota(quotaId);
    }
  };

  const handleViewHistory = (supplierId: number, supplierName: string) => {
    setSelectedSupplier({ id: supplierId, name: supplierName });
    onHistoryOpen();
  };

  // Set page title
  useEffect(() => {
    dispatch(changeTitle('CRM Supplier Contact Quotas'));
  }, [dispatch]);

  const columns = React.useMemo(
    () => [
      {
        Header: (
          <TableHeader
            name='Supplier'
            sortButtonComponent={<BaseSortButton sortBy='supplierId' />}
            filterButtonComponent={<SupplierFilterButton />}
          />
        ),
        accessor: 'supplier',
        width: '35%',
        Cell: ({ value }: CellProps<any, any>) => <CustomTableCell as={SupplierCell} {...value} />,
      },
      {
        Header: <TableHeader name='Quota Information' filterButtonComponent={<QuotaRangeFilterButton />} />,
        accessor: 'quotaInfo',
        width: '30%',
        Cell: ({ value }: CellProps<any, any>) => <CustomTableCell as={QuotaInfoCell} {...value} />,
      },
      {
        Header: <TableHeader name='Created Date' sortButtonComponent={<BaseSortButton sortBy='createdAt' />} />,
        accessor: 'createdAt',
        width: '20%',
        Cell: ({ value }: CellProps<any, string>) => (
          <div style={{ height: '80px', display: 'flex', alignItems: 'center', padding: '8px 0' }}>
            <Text fontSize='sm' color='gray.700'>
              {new Date(value).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
              })}
            </Text>
          </div>
        ),
      },
      {
        Header: <TableHeader name='Actions' />,
        accessor: 'actions',
        width: '15%',
        Cell: ({ value }: CellProps<any, any>) => <CustomTableCell as={ActionsCell} {...value} />,
      },
    ],
    [isDeleting]
  );

  const tableData = React.useMemo(
    () =>
      quotasData?.map((item: ICrmSupplierContactQuotaData) => ({
        id: item.id,
        supplier: {
          supplier: item.supplier,
        },
        quotaInfo: {
          quota: item.quota,
          remaining: item.remaining,
          used: item.used,
          usagePercentage: item.usagePercentage,
        },
        createdAt: item.createdAt,
        actions: {
          quotaId: item.id,
          supplierId: item.supplierId,
          supplierName: item.supplier?.name || 'Unknown Supplier',
          onView: handleViewQuota,
          onEdit: handleEditQuota,
          onDelete: handleDeleteQuota,
          onViewHistory: handleViewHistory,
        },
      })) || [],
    [quotasData, isDeleting]
  );

  return (
    <>
      <BaseTableContainer isLoading={isLoading}>
        <BaseTableContainer.Header>
          <BaseTableContainer.Header.Left>
            <BaseTableTitle title='CRM Supplier Contact Quotas' />
            <BaseTableSpinner isFetching={isFetching} />
          </BaseTableContainer.Header.Left>

          <BaseTableContainer.Header.Right>
            <Tooltip label='Create new quota'>
              <IconButton
                aria-label='Create new quota'
                icon={<FiPlus />}
                colorScheme='blue'
                onClick={onCreateOpen}
                mr={3}
              />
            </Tooltip>
            <ClearFilterButton />
          </BaseTableContainer.Header.Right>
        </BaseTableContainer.Header>

        <Box px={6} py={4}>
          <StatsCards />
        </Box>

        <BaseTableContainer.TableWrapper>
          <BaseTable columns={columns} data={tableData} />
        </BaseTableContainer.TableWrapper>

        <BaseTableContainer.Footer>
          <BaseTableContainer.Footer.Left />
          <BaseTableContainer.Footer.Right>
            <BasePagination>
              <BasePagination.Pages pagesCount={pageCount ?? 0} setSearchParams={setPaginationParams} />
              <BasePagination.Limit total={total ?? 0} setSearchParams={setPaginationParams} />
            </BasePagination>
          </BaseTableContainer.Footer.Right>
        </BaseTableContainer.Footer>
      </BaseTableContainer>

      {/* Modals */}
      <CreateQuotaModal isOpen={isCreateOpen} onClose={onCreateClose} />

      {selectedQuotaId && (
        <>
          <ViewQuotaModal isOpen={isViewOpen} onClose={onViewClose} quotaId={selectedQuotaId} />
          <EditQuotaModal isOpen={isEditOpen} onClose={onEditClose} quotaId={selectedQuotaId} />
        </>
      )}

      {selectedSupplier && (
        <ContactHistoryModal
          isOpen={isHistoryOpen}
          onClose={onHistoryClose}
          supplierId={selectedSupplier.id}
          supplierName={selectedSupplier.name}
        />
      )}
    </>
  );
};

export default CrmSupplierContactQuotasListScreen;
